// useCollaborationData — Scope-aware data fetching for Collaboration quadrants
// Task #1867 — User Story #1863

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { CollaborationScope, CollaborationData, CollaborationContact, CollaborationOrg, CollaborationLink, CollaborationTodo } from '../types/collaboration'
import type { DocumentListItem, FolderListItem, CrmContact, CrmOrganisation, CrmTodo, CrmContactOrg, DisclosureAgreement } from '../types'
import {
  getDocumentListItems, getFolderListItems,
  getCrmContactOrgs, getCrmTodos,
  getCollaborationRollup, getDisclosureAgreements,
} from '../services/api'
import { fieldDecryptFailed } from '../utils/encryption'

// BUG: the Research Network / CRM project tab crashed the whole app with
// "Cannot read properties of undefined (reading 'toLowerCase')" whenever a
// project's CRM organisation/contact record had a field the backend
// fail-closed on (BUG-135 pattern — envelope.py REMOVES an undecryptable
// field rather than returning ciphertext, e.g. `_decrypt_error: true`,
// `_decrypt_failed_fields: ["org_name"]`). `normaliseCrmOrg`/`normaliseCrmContact`
// below then produced a CollaborationOrg/Contact with `name: undefined`
// despite the type declaring `name: string`, and useCollaborationFilter's
// alphabetical sort (`a.name.toLowerCase()`) crashed on it. Real production
// data hits this: a project with a genuine collaborator ("ZTA 2.5") has CRM
// org rows whose `org_name` failed to decrypt (KeyScopeMismatch). Give an
// honest, non-crashing label instead of ever letting `name` end up undefined.
function safeOrgName(o: CrmOrganisation): string {
  if (fieldDecryptFailed(o, 'org_name')) return 'Organisation (name unavailable)'
  return o.org_name || 'Unnamed organisation'
}

function safeContactName(c: CrmContact): string {
  if (fieldDecryptFailed(c, 'first_name') && fieldDecryptFailed(c, 'last_name')) return 'Contact (name unavailable)'
  const full = `${c.first_name || ''} ${c.last_name || ''}`.trim()
  return full || 'Unnamed contact'
}

// Extract projectId from any scope
function getProjectId(scope: CollaborationScope): string {
  return scope.projectId
}

// Build the entity_type_id for todo queries
function getTodoEntityTypeId(scope: CollaborationScope): string {
  switch (scope.type) {
    case 'document': return `document#${scope.documentId}`
    case 'folder': return `folder#${scope.folderId}`
    case 'project': return `project#${scope.projectId}`
  }
}

// Normalise document/folder list items to CollaborationContact
function normaliseListItemContact(item: DocumentListItem | FolderListItem): CollaborationContact {
  return { id: item.resource_id, name: item.title, role: '', email: '', orgId: undefined, orgName: undefined, notes: item.notes || '' }
}

// Normalise CrmContact to CollaborationContact
function normaliseCrmContact(c: CrmContact): CollaborationContact {
  return { id: c.contact_id, name: safeContactName(c), role: c.role || '', email: c.email || '', orgId: undefined, orgName: c.organisation_name, source: c.source || undefined, centralDirectoryId: c.central_directory_id || undefined }
}

// Normalise document/folder list items to CollaborationOrg
function normaliseListItemOrg(item: DocumentListItem | FolderListItem): CollaborationOrg {
  return { id: item.resource_id, name: item.title, industry: item.notes || '', contactCount: 0, notes: item.notes || '' }
}

// Normalise CrmOrganisation to CollaborationOrg
function normaliseCrmOrg(o: CrmOrganisation, contactOrgLinks: CrmContactOrg[]): CollaborationOrg {
  const contactCount = contactOrgLinks.filter(l => l.org_id === o.org_id).length
  return { id: o.org_id, name: safeOrgName(o), industry: o.industry || '', contactCount, source: o.source || undefined, centralDirectoryId: o.central_directory_id || undefined }
}

// Normalise list items to CollaborationLink
function normaliseListItemLink(item: DocumentListItem | FolderListItem): CollaborationLink {
  const isPatent = item.title?.match(/^(AU|US|EP|WO|CN|JP|KR|GB|DE|FR|CA|IN)\d|patent/i) != null
  return {
    id: item.resource_id,
    title: item.title,
    type: isPatent ? 'patent' : 'journal',
    doi: undefined,
    filing: undefined,
    similarityScore: undefined,
    saved: true,
    contactIds: [],
    orgIds: [],
    notes: item.notes || '',
  }
}

// Derive the associated contact/org from a todo's entity_type_id.
// The PK is encoded as "<type>#<id>", e.g. "contact#<contact_id>" or
// "org#<org_id>" (also "document#…", "folder#…", "project#…", "user#…").
// BUG-138: previously contactIds/orgIds were hard-coded to [], so any
// org/contact selection filtered every todo out. Populate them from the real
// association carried in entity_type_id so selection filtering works.
function todoAssociations(entityTypeId: string): { contactIds: string[]; orgIds: string[] } {
  const idx = (entityTypeId || '').indexOf('#')
  if (idx <= 0) return { contactIds: [], orgIds: [] }
  const prefix = entityTypeId.slice(0, idx)
  const id = entityTypeId.slice(idx + 1)
  if (!id) return { contactIds: [], orgIds: [] }
  if (prefix === 'contact') return { contactIds: [id], orgIds: [] }
  if (prefix === 'org' || prefix === 'organisation') return { contactIds: [], orgIds: [id] }
  return { contactIds: [], orgIds: [] }
}

// Normalise CrmTodo to CollaborationTodo
function normaliseCrmTodo(t: CrmTodo): CollaborationTodo {
  const { contactIds, orgIds } = todoAssociations(t.entity_type_id)
  return {
    id: t.todo_id,
    entityTypeId: t.entity_type_id,
    title: t.title,
    description: t.description || '',
    status: t.status,
    priority: t.priority,
    dueDate: t.due_date || '',
    assigneeId: t.assignee_id || '',
    contactIds,
    orgIds,
  }
}

export function useCollaborationData(scope: CollaborationScope): CollaborationData {
  const projectId = getProjectId(scope)
  const todoEntityTypeId = getTodoEntityTypeId(scope)

  // === Project scope: use single rollup endpoint ===
  const rollupQ = useQuery({
    queryKey: ['collab', 'rollup', projectId],
    queryFn: () => getCollaborationRollup(projectId),
    enabled: scope.type === 'project',
    retry: 2,
  })

  // Disclosure-derived collaborators: an agreement that has reached Level 6
  // (Full Collaboration) is a real, established collaborator. The agreement is
  // the single source of truth (it already carries the counterparty identity at
  // L4+), so we surface it directly in the Collaboration tab rather than copying
  // it into the CRM. (BUG 4e — the connection existed but nothing rendered it.)
  const disclosureQ = useQuery({
    queryKey: ['collab', 'disclosure', projectId],
    queryFn: () => getDisclosureAgreements(projectId),
    enabled: scope.type === 'project' && !!projectId,
    retry: 1,
  })

  // BUG-011: Wrap document-level fetches to gracefully handle 404s (backend route
  // may not exist at document level). Returns empty data instead of throwing.
  const safeFetchDocList = async (documentId: string, resourceType: string) => {
    try {
      return await getDocumentListItems(documentId, resourceType)
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.status === 404) {
        return { items: [] }
      }
      throw err
    }
  }

  // === Document scope queries ===
  const docContactsQ = useQuery({
    queryKey: ['collab', 'doc-contacts', scope.type === 'document' ? scope.documentId : ''],
    queryFn: () => safeFetchDocList((scope as { documentId: string }).documentId, 'contact'),
    enabled: scope.type === 'document',
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 404 || err?.status === 404) return false
      return failureCount < 2
    },
  })

  const docOrgsQ = useQuery({
    queryKey: ['collab', 'doc-orgs', scope.type === 'document' ? scope.documentId : ''],
    queryFn: () => safeFetchDocList((scope as { documentId: string }).documentId, 'organisation'),
    enabled: scope.type === 'document',
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 404 || err?.status === 404) return false
      return failureCount < 2
    },
  })

  const docLinksQ = useQuery({
    queryKey: ['collab', 'doc-links', scope.type === 'document' ? scope.documentId : ''],
    queryFn: () => safeFetchDocList((scope as { documentId: string }).documentId, 'journal'),
    enabled: scope.type === 'document',
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 404 || err?.status === 404) return false
      return failureCount < 2
    },
  })

  // === Folder scope queries ===
  const folderContactsQ = useQuery({
    queryKey: ['collab', 'folder-contacts', scope.type === 'folder' ? scope.folderId : '', projectId],
    queryFn: () => getFolderListItems(projectId, (scope as { folderId: string }).folderId, 'contact', undefined),
    enabled: scope.type === 'folder',
  })

  const folderOrgsQ = useQuery({
    queryKey: ['collab', 'folder-orgs', scope.type === 'folder' ? scope.folderId : '', projectId],
    queryFn: () => getFolderListItems(projectId, (scope as { folderId: string }).folderId, 'organisation', undefined),
    enabled: scope.type === 'folder',
  })

  const folderLinksQ = useQuery({
    queryKey: ['collab', 'folder-links', scope.type === 'folder' ? scope.folderId : '', projectId],
    queryFn: () => getFolderListItems(projectId, (scope as { folderId: string }).folderId, 'journal', undefined),
    enabled: scope.type === 'folder',
  })

  // === Shared queries (document + folder scopes only — project uses rollup) ===
  const contactOrgsQ = useQuery({
    queryKey: ['collab', 'contact-orgs', projectId],
    queryFn: () => getCrmContactOrgs(projectId),
    enabled: scope.type !== 'project' && !!projectId,
  })

  const todosQ = useQuery({
    queryKey: ['collab', 'todos', todoEntityTypeId],
    queryFn: () => getCrmTodos(todoEntityTypeId),
    enabled: scope.type !== 'project' && !!todoEntityTypeId,
  })

  // === Normalise data based on scope ===
  const contacts = useMemo<CollaborationContact[]>(() => {
    if (scope.type === 'document') return (docContactsQ.data?.items || []).map(normaliseListItemContact)
    if (scope.type === 'folder') return (folderContactsQ.data?.items || []).map(normaliseListItemContact)
    // Project scope: use rollup data
    return (rollupQ.data?.contacts || []).map(normaliseCrmContact)
  }, [scope.type, docContactsQ.data, folderContactsQ.data, rollupQ.data])

  const contactOrgLinks = useMemo(() => {
    if (scope.type === 'project') return rollupQ.data?.contact_org_links || []
    return contactOrgsQ.data?.contact_org_links || []
  }, [scope.type, contactOrgsQ.data, rollupQ.data])

  // Collaborators established via a completed (Level 6) disclosure agreement.
  const disclosureOrgs = useMemo<CollaborationOrg[]>(() => {
    return ((disclosureQ.data as DisclosureAgreement[] | undefined) || [])
      .filter(a => (a.current_level ?? 0) >= 6 && a.counterparty_org_id)
      .map(a => ({
        id: a.counterparty_org_id as string,
        name: a.counterparty_name || a.counterparty_email || 'Collaborating organisation',
        industry: 'Disclosure collaboration (Full Collaboration)',
        contactCount: 0,
        source: 'disclosure',
      }))
  }, [disclosureQ.data])

  const orgs = useMemo<CollaborationOrg[]>(() => {
    if (scope.type === 'document') return (docOrgsQ.data?.items || []).map(normaliseListItemOrg)
    if (scope.type === 'folder') return (folderOrgsQ.data?.items || []).map(normaliseListItemOrg)
    // Project scope: rollup CRM orgs + disclosure-derived collaborators (deduped by id).
    const crmOrgs = (rollupQ.data?.organisations || []).map(o => normaliseCrmOrg(o, contactOrgLinks))
    const byId = new Map(crmOrgs.map(o => [o.id, o]))
    for (const d of disclosureOrgs) if (!byId.has(d.id)) byId.set(d.id, d)
    return Array.from(byId.values())
  }, [scope.type, docOrgsQ.data, folderOrgsQ.data, rollupQ.data, contactOrgLinks, disclosureOrgs])

  const links = useMemo<CollaborationLink[]>(() => {
    if (scope.type === 'document') return (docLinksQ.data?.items || []).map(normaliseListItemLink)
    if (scope.type === 'folder') return (folderLinksQ.data?.items || []).map(normaliseListItemLink)
    // Project scope: use rollup links
    return (rollupQ.data?.links || []).map(l => ({
      id: l.link_id,
      title: l.title,
      type: l.type,
      doi: undefined,
      filing: undefined,
      similarityScore: undefined,
      saved: true,
      contactIds: [],
      orgIds: [],
      notes: l.notes || '',
    }))
  }, [scope.type, docLinksQ.data, folderLinksQ.data, rollupQ.data])

  const todos = useMemo<CollaborationTodo[]>(() => {
    if (scope.type === 'project') {
      const crmTodos = (rollupQ.data?.todos || []).map(normaliseCrmTodo)
      // Merge WhatNext suggestions as suggested todos
      const suggestions: CollaborationTodo[] = (rollupQ.data?.suggestions || []).map(s => ({
        id: s.action_id,
        entityTypeId: `document#${s.document_id}`,
        title: (s.document_count ?? 1) > 1 ? `${s.title} (${s.document_count} docs)` : s.title,
        description: s.description,
        status: 'suggested' as CollaborationTodo['status'],
        priority: s.priority <= 3 ? 'high' as CollaborationTodo['priority'] : 'medium' as CollaborationTodo['priority'],
        dueDate: '',
        assigneeId: '',
        contactIds: [],
        orgIds: [],
      }))
      return [...crmTodos, ...suggestions]
    }
    return (todosQ.data?.todos || []).map(normaliseCrmTodo)
  }, [scope.type, todosQ.data, rollupQ.data])

  // === Loading state ===
  const isLoading = useMemo(() => {
    if (scope.type === 'document') return docContactsQ.isLoading || docOrgsQ.isLoading || docLinksQ.isLoading || todosQ.isLoading || contactOrgsQ.isLoading
    if (scope.type === 'folder') return folderContactsQ.isLoading || folderOrgsQ.isLoading || folderLinksQ.isLoading || todosQ.isLoading || contactOrgsQ.isLoading
    return rollupQ.isLoading
  }, [scope.type, docContactsQ.isLoading, docOrgsQ.isLoading, docLinksQ.isLoading, folderContactsQ.isLoading, folderOrgsQ.isLoading, folderLinksQ.isLoading, rollupQ.isLoading, todosQ.isLoading, contactOrgsQ.isLoading])

  // === Error state ===
  const error = useMemo<Error | null>(() => {
    if (scope.type === 'document') {
      return (docContactsQ.error || docOrgsQ.error || docLinksQ.error || todosQ.error || contactOrgsQ.error) as Error | null
    }
    if (scope.type === 'folder') {
      return (folderContactsQ.error || folderOrgsQ.error || folderLinksQ.error || todosQ.error || contactOrgsQ.error) as Error | null
    }
    return (rollupQ.error as Error | null)
  }, [scope.type, docContactsQ.error, docOrgsQ.error, docLinksQ.error, folderContactsQ.error, folderOrgsQ.error, folderLinksQ.error, rollupQ.error, todosQ.error, contactOrgsQ.error])

  return { contacts, orgs, links, todos, contactOrgLinks, isLoading, error }
}
